<template>
  <div>

    <div class="machining-option__image-tagline">
      <span>{{$t('image-not-to-scale')}}</span>
    </div>

    <div class="machining-option__image-container">
      <div v-if="showImageLoader" class="image-loading-spinner">
        <half-circle-spinner
          :animation-duration="1000"
          :size="60"
          color="#b5b5b5"
        />
      </div>
      <img class="machining-option__image" :src="option.modalImage" :class="{'machining-option__image--loading': showImageLoader}" @load="showImageLoader = false">
    </div>

    <form action="#" @submit.prevent="" class="machining-option__variables row">
      <MachiningOptionVariable 
        v-for="variable in option.variables"
        :key="variable.varName"
        ref="machiningOptionVariableComponents"
        :data="variable"
        @valueHasChanged="valueHasChanged"
        @helpIconClicked="openHelpModal"
      />
    </form>

    <div class="machining-option__footer">
      <div v-if="showCalculationError" id="calculation-error-popup" class="tooltip-popup tooltip-popup--error">
        <span class="glyphicon glyphicon-remove tooltip-popup__close-icon" @click="showCalculationError = false"></span>
        <span class="tooltip-popup__title">{{$t('alert')}}</span>
        <p>{{ $t('error.calculation')}}</p>
      </div>

      <div class="buttons">
        <button class="button button--md button--full-width button--secondary" @click="onCancelButtonClicked()">{{$t('cancel')}}</button>
        <button class="button button--md button--full-width" :class="{'button--disabled': !readyToSubmit}" @click="readyToSubmit ? onSaveButtonClicked() : null">{{$t('save')}}</button>
      </div>
    </div>

  </div>
</template>

<script>

import MachiningOptionVariable from "./MachiningOptionVariable.vue"

import { HalfCircleSpinner } from "epic-spinners";

var mexp = require('math-expression-evaluator');

export default {
  name: 'MachiningOption',
  components: {
    MachiningOptionVariable,
    HalfCircleSpinner,
  },
  props: {
    option: Object,
  },
  data: () => {
    return {
      publicPath: process.env.BASE_URL,

      showImageLoader: true,

      readyToSubmit: false,

      showCalculationError: false,
    }
  },
  mounted() {
    this.updateVariableRangesAndValues();
  },
  computed: {
  },
  methods: {
    
    updateVariableRangesAndValues() {
      // Apply value overrides from select variables that override other variables
      this.applyOptionVariableValueOverrides();
      
      // Apply value overrides from input variables that override other variables
      this.applyInputVariableValueOverrides();

      // Calculate min/max formulas
      this.calculateMinMaxFormulas();
      
      // Apply min/max overrides from select variables that override other variables
      this.applyOptionVariableRangeOverrides();

      // After all min/maxes are set, check if there are any problems with them
      this.checkValidityOfVariableRanges();
    },

    applyOptionVariableValueOverrides() {
      // Certain options such as Radial Hole With Counterbore have readonly variables that are populated when a separate variable is selected.

      this.$refs.machiningOptionVariableComponents.forEach(component => {
        // If this variable is a select field and a value has been selected
        if (component.optionType == 'select' && component.selectValue) {
          // Get the option object
          let selectedOption = component.data.options.find(x => x.code == component.selectValue);
          // If we find a corresponding option object and it has variable overrides...
          if (selectedOption && selectedOption.variables != null && Object.keys(selectedOption.variables).length) {
            for (let variableCode in selectedOption.variables) {
              // Set the inputValue of the corresponding variable component

              // The MachiningOptionVariable component uses a local variable for the actual interaction by the user, 
              // which is initialized from the machining option decimalValue in mounted(). To change the value that
              // the user sees in the input field, this is the variable we need to update.
              for (let i = 0; i < this.$refs.machiningOptionVariableComponents.length; i++) {
                if (this.option.variables[i].code == variableCode) {
                  this.$refs.machiningOptionVariableComponents[i].inputValue = +selectedOption.variables[variableCode];
                }
              }
            }
          }
        }
      });
    },

    applyInputVariableValueOverrides() {
      this.$refs.machiningOptionVariableComponents.forEach(component => {
        if (component.data.variableOverrides != null && component.data.variableOverrides.length) {
          let variableOverride = component.data.variableOverrides.find(x => (x.minVal == null || component.inputValue >= x.minVal) && (x.maxVal == null || component.inputValue <= x.maxVal));
          if (variableOverride) {
            let finalFormula = this.replaceFormulaPlaceholders(variableOverride.value);
            if (finalFormula != null) {
              try {
                  let value = mexp.eval(finalFormula);
                  if (!Number.isNaN(value) && value != Infinity && value >= 0) {
                    let targetVariableIndex = this.option.variables.findIndex(x => x.varName == variableOverride.targetVariable);
                    if (targetVariableIndex) {
                      this.$refs.machiningOptionVariableComponents[targetVariableIndex].inputValue = value;
                    }
                  }
              } catch { }
            }
          }
        }
      });
    },

    calculateMinMaxFormulas() {
      for (let i = 0; i < this.option.variables.length; i++) {
        if (this.option.variables[i].minFormulas != null && this.option.variables[i].minFormulas.length) {
          let minValues = this.getCalculatedArrayFromFormulas(this.option.variables[i].minFormulas);
          let precision = this.option.variables[i].precision != null ? this.option.variables[i].precision : 3;
          this.option.variables[i].min = this.roundAwayFromZero(Math.max(...minValues), precision);
        }
        
        if (this.option.variables[i].maxFormulas != null && this.option.variables[i].maxFormulas.length) {
          let maxValues = this.getCalculatedArrayFromFormulas(this.option.variables[i].maxFormulas);
          let precision = this.option.variables[i].precision != null ? this.option.variables[i].precision : 3;
          this.option.variables[i].max = this.roundTowardsZero(Math.min(...maxValues), precision);
        }
      }
    },
    getCalculatedArrayFromFormulas(formulas) {
      let values = [];
      formulas.forEach(formula => {
        // First, before calculating the formula, we will check if it contains any placeholder values.
        // Placeholders are the varName of another variable enclosed by curly braces, e.g.: {T1}
        let finalFormula = this.replaceFormulaPlaceholders(formula);

        try {
          if (finalFormula != null) {
            let value = mexp.eval(finalFormula);
            if (!Number.isNaN(value) && value != Infinity && value >= 0) {
              values.push(value);
            }
          }
        } catch { }

      });

      return values;
    },
    replaceFormulaPlaceholders(formula) {
      let placeholders = formula.match(/{(\w+?)}/g);
      if (placeholders) {
        // If the formula does contain placeholders, we will attempt to replace the placeholders
        // with the number value from the variables referenced by the placeholders.

        // Remove duplicates
        placeholders = [...new Set(placeholders)];

        placeholders.every(placeholder => {
          let replacementValue = null;

          let varName = placeholder.substring(1, placeholder.length - 1);
          let replacementVariableIndex = this.option.variables.findIndex(x => x.varName == varName);
          if (replacementVariableIndex != -1) {
            
            if (this.option.variables[replacementVariableIndex].isDropDown) {
              // If it's a dropdown, get the value of the selected option
              let selectedOptionCode = this.$refs.machiningOptionVariableComponents[replacementVariableIndex].selectValue;
              let selectedOption = this.option.variables[replacementVariableIndex].options.find(x => x.code == selectedOptionCode);
              if (selectedOption) {
                replacementValue = selectedOption.value;
              }
            } else {
              // Otherwise, use the inputValue from the component
              let variableValue = this.$refs.machiningOptionVariableComponents[replacementVariableIndex].inputValue;

              // Since this is a user-entered field, make sure the number is valid within the min and max range (if they are not null)
              if ((!this.option.variables[replacementVariableIndex].min || variableValue >= this.option.variables[replacementVariableIndex].min) && 
                  (!this.option.variables[replacementVariableIndex].max || variableValue <= this.option.variables[replacementVariableIndex].max)) {
                replacementValue = variableValue;
              }
            }

          }

          if (replacementValue != null) {
            // If the variable referenced by the placeholder has a value, replace the placeholder with the value
            let regex = new RegExp(placeholder, 'g');
            formula = formula.replace(regex, replacementValue);
            return true;
          } else {
            // Otherwise, we will not use this formula
            formula = null;
            return false;
          }

        });
      }
      return formula;
    },
    roundAwayFromZero(value, decimals) {
      return Number(Math.ceil(value+'e'+decimals)+'e-'+decimals);
    },
    roundTowardsZero(value, decimals) {
      return Number(Math.floor(value+'e'+decimals)+'e-'+decimals);
    },

    applyOptionVariableRangeOverrides() {
      // Certain select options may have range min/max values that overwrite the default min/max for another variable.

      this.$refs.machiningOptionVariableComponents.forEach(component => {
        // If this variable is a select field and a value has been selected
        if (component.optionType == 'select' && component.selectValue) {
          // Get the option object
          let selectedOption = component.data.options.find(x => x.code == component.selectValue);

          // If we find a corresponding option object and it has range min overrides...
          if (selectedOption && selectedOption.variablesMins != null && Object.keys(selectedOption.variablesMins).length) {
            for (let variableCode in selectedOption.variablesMins) {
              // Set the range min of the corresponding variable
              let correspondingVariable = this.option.variables.find(x => x.code == variableCode);
              if (correspondingVariable) {
                // Only override if the new value is greater than the existing value
                correspondingVariable.min = Math.max(+selectedOption.variablesMins[variableCode], correspondingVariable.min);
              }
            }
          }

          // If we find a corresponding option object and it has range max overrides...
          if (selectedOption && selectedOption.variablesMaxs != null && Object.keys(selectedOption.variablesMaxs).length) {
            for (let variableCode in selectedOption.variablesMaxs) {
              // Set the range max of the corresponding variable
              let correspondingVariable = this.option.variables.find(x => x.code == variableCode);
              if (correspondingVariable) {
                // Only override if the new value is less than the existing value
                correspondingVariable.max = Math.min(+selectedOption.variablesMaxs[variableCode], correspondingVariable.max);
              }
            }
          }

        }
      });
    },

    checkValidityOfVariableRanges() {
      for (let i = 0; i < this.$refs.machiningOptionVariableComponents.length; i++) {
        if (this.$refs.machiningOptionVariableComponents[i].optionType == 'input' && this.option.variables[i].min != null && this.option.variables[i].max != null && // Only check when the variable is an input and has min and max values
            this.option.variables[i].min < 0 || this.option.variables[i].min == Infinity || isNaN(this.option.variables[i].min) || // Check if min is negative, Infinity, or NaN
            this.option.variables[i].max < 0 || this.option.variables[i].max == Infinity || isNaN(this.option.variables[i].max) || // Check if max is negative, Infinity, or NaN
            this.option.variables[i].max < this.option.variables[i].min) { // Check if max is less than min
            this.showCalculationError = true;
            break;
        } else {
          this.showCalculationError = false;
        }
      };
    },
    

    valueHasChanged(valueChangedIsValid) {
      if (!valueChangedIsValid) {
        this.readyToSubmit = false;
        return;
      }
      
      // Some min/max formulas depend on the value of another variable, so anytime any variable changes we need to recalculate
      this.updateVariableRangesAndValues();

      // Re-check validity of all variables in case the min/maxes changed and they are no longer valid
      this.readyToSubmit = this.checkValidityOfVariableValues();
    },

    checkValidityOfVariableValues() {
      return this.$refs.machiningOptionVariableComponents.every(component => {
        return component.data.readOnly == true ? true : component.checkValidity();
      });
    },
    
    onCancelButtonClicked() {
      this.$emit('cancelButtonClicked');
      
      this.$store.dispatch("trackEvent", {
        action: "Click - Cancel Machining Option",
        label: this.option.name
      })
    },

    onSaveButtonClicked() {
      this.$refs.machiningOptionVariableComponents.forEach(component => {
        component.updateVariableValue();
      });

      this.option.isApplied = true;
      this.$emit('saveButtonClicked');

      this.$store.dispatch("trackEvent", {
        action: "Click - Save Machining Option",
        label: this.option.name
      })

    },

    openHelpModal(variableVarName) {
      this.$emit('openHelpModal', variableVarName);
    },
  }
}
</script>

<style scoped lang="scss">
.thomson-app {

  .machining-option__image-tagline {
    font-size: 13px;
    color: #666766;
  }

  .machining-option__image-container {
    display: flex;
    position: relative;
    margin-bottom: 20px;
  }
  .machining-option__image {
    margin: auto;
    width: auto;
    height: auto;
    max-height: 265px;
    max-width: 100%;
  }
  .machining-option__image--loading {
    min-height: 265px;
  }
  .image-loading-spinner {
    position: absolute;
    left: calc(50% - 30px);
    top: calc(50% - 30px);
    z-index: 3;
  }

  .machining-option__variables {
    margin-bottom: 20px;
  }

  .machining-option__footer {
    border-top: 1px solid #797979;
    padding-top: 20px;
    position: relative;
  }
  .machining-option__footer .button {
    max-width: 140px;
  }
  .buttons {
    justify-content: center;
  }

  #calculation-error-popup {
    max-width: 98%;
    margin-left: 1%;
    bottom: 90%;
  }
}
</style>
